import * as React from 'react'
import { Alert, Box, Paper, Stack, Typography } from '@mui/material'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { TChargebackDispute } from '../../core/types/ChargebackDispute'
import {
  daysUntil,
  disputeCycleLabel,
  disputeEventLabel,
  disputeReasonLabel,
  formatBrasilia,
  formatBrasiliaParts,
  isAwaitingEvidence,
  remainingDaysLabel,
} from '../../core/functions/chargebackDispute'
import { useIsDisputeAdminView } from '../../core/context/TenantSettingsContext'
import NumberFunctions from '../../core/functions/NumberFunctions'

function InfoRow({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" textAlign="right">
        {value || '—'}
      </Typography>
    </Stack>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      <Stack spacing={1}>{children}</Stack>
    </Paper>
  )
}

interface DisputeDetailContentProps {
  dispute: TChargebackDispute
  // No detalhe da transação a venda já está nas outras abas
  hideSale?: boolean
}

export default function DisputeDetailContent({
  dispute,
  hideSale,
}: DisputeDetailContentProps) {
  const isAdminView = useIsDisputeAdminView()
  const deadline = formatBrasiliaParts(dispute.responseDeadline)
  const days = daysUntil(dispute.responseDeadline)
  // canSubmitEvidence vem sempre false para o admin
  const showDeadline =
    dispute.canSubmitEvidence || isAwaitingEvidence(dispute)
  const events = [...dispute.events].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )

  return (
    <Stack spacing={2}>
      {showDeadline && (
        <Alert severity={days <= 2 ? 'error' : 'warning'}>
          {deadline
            ? `${isAdminView ? 'O produtor tem' : 'Você tem'} até ${deadline.date} às ${deadline.time} (horário de Brasília) para enviar as provas. ${remainingDaysLabel(days)}.`
            : 'Prazo encerrado'}
        </Alert>
      )}

      {dispute.tenantName && (
        <Section title="Produtor">
          <InfoRow label="Nome" value={dispute.tenantName} />
        </Section>
      )}

      {!hideSale && (
        <Section title="Venda">
          <InfoRow label="Pedido" value={dispute.orderCode} />
          <InfoRow
            label="Cliente"
            value={
              dispute.customerName && (
                <>
                  {dispute.customerName}
                  {dispute.customerEmail && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {dispute.customerEmail}
                    </Typography>
                  )}
                </>
              )
            }
          />
          <InfoRow label="Produto" value={dispute.productName} />
          <InfoRow label="Bandeira" value={dispute.network} />
          <InfoRow label="Cobrança" value={dispute.chargeId} />
        </Section>
      )}

      <Section title="Disputa">
        <InfoRow
          label="Valor contestado"
          value={NumberFunctions.formatMoney(dispute.chargebackAmount, true)}
        />
        {dispute.debitedAmount !== dispute.chargebackAmount && (
          <InfoRow
            label="Valor debitado"
            value={NumberFunctions.formatMoney(dispute.debitedAmount, true)}
          />
        )}
        <InfoRow
          label="Motivo"
          value={`${disputeReasonLabel(dispute.reasonDescription)} (${dispute.reasonCode})`}
        />
        {hideSale && <InfoRow label="Bandeira" value={dispute.network} />}
        <InfoRow label="Etapa" value={dispute.stage} />
        <InfoRow
          label="Ciclo"
          value={dispute.cycle && disputeCycleLabel(dispute.cycle)}
        />
        <InfoRow label="Status do ciclo" value={dispute.cycleStatus} />
        <InfoRow
          label="Recebido em"
          value={formatBrasilia(dispute.gatewayCreatedAt)}
        />
      </Section>

      <Section title="Linha do tempo">
        {events.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nenhum evento registrado
          </Typography>
        )}
        {events.map((event, index) => (
          <Stack
            key={`${event.type}-${event.createdAt}`}
            direction="row"
            spacing={1.5}
          >
            <Stack alignItems="center">
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  mt: 0.75,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                }}
              />
              {index < events.length - 1 && (
                <Box sx={{ flex: 1, width: 2, bgcolor: 'divider' }} />
              )}
            </Stack>
            <Box sx={{ pb: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {disputeEventLabel(event.type)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatBrasilia(event.createdAt)}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Section>

      {dispute.evidenceSentAt && (
        <Section title="Provas enviadas">
          <Typography variant="body2" color="text.secondary">
            Enviadas em {formatBrasilia(dispute.evidenceSentAt)}
          </Typography>
          {dispute.evidenceFileNames.map((name) => (
            <Stack key={name} direction="row" spacing={1} alignItems="center">
              <PictureAsPdfIcon fontSize="small" color="action" />
              <Typography variant="body2">{name}</Typography>
            </Stack>
          ))}
        </Section>
      )}
    </Stack>
  )
}
